import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch/Switch';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';

import DataGridTable from '@/shared/components/dataDisplay/DataGridTable/DataGridTable';
import { AppPath } from '@/shared/constants/path';
import TableInstance from '@/shared/constants/tableInstance';
import useModalDialog from '@/shared/hooks/useModalDialog';
import DialogName from '@/shared/types/dialogName.enum';
import Product from '@/shared/types/products/product.type';

import MapInventoryToProductDialog from './components/MapInventoryToProductDialog/MapInventoryToProductDialog';
import UnMapInventoryConfirmationDialog from './components/UnMapInventoryConfirmationDialog';
import useInventoryTable from './hooks/useInventoryTable';
import useInventoryTableActions from './hooks/useInventoryTableActions';
import InventoryTableRow from './types/inventoryTableRow.type';

type InventoryTableProps = {
  product: Product | null;
};

const InventoryTable: FC<InventoryTableProps> = ({ product }) => {
  const { t } = useTranslation(['products', 'common']);

  const { dialog, onUpdateDialogState } = useModalDialog<unknown, DialogName>();

  const {
    refreshTableIndex,
    onIsSellOutOfStockUpdate,
    handleUnMap,
    handleMapInventoryToProduct,
  } = useInventoryTableActions({
    product,
    onUpdateDialogState,
  });

  const {
    model,
    table,
    apiRef,
    columns,
    productRef,
    visibilityModel,
    setVisibilityModel,
  } = useInventoryTable({ product, refreshTableIndex });

  const createRenderCell = (params: GridRenderCellParams) => {
    const row = params.row as Record<string, string>;
    const key = params.field;

    return (
      <Switch
        checked={row[key] as unknown as boolean}
        onChange={(e) => {
          void onIsSellOutOfStockUpdate({
            productId: row.product_id,
            variantId: row.variant_id,
            is_sell_out_of_stock: e.target.checked,
          });
        }}
      />
    );
  };

  const customizedColumns: GridColDef[] = columns.map((column) => {
    switch (column.field) {
      case 'is_sell_out_of_stock':
        return {
          ...column,
          renderCell(params) {
            return createRenderCell(params);
          },
        };
      default:
        return column;
    }
  });

  return (
    <>
      <div style={{ height: '60vh', width: '100%' }}>
        <DataGridTable
          getRowId={(item) => item.variant_id as string}
          columns={customizedColumns}
          rows={table.data}
          rowCount={table.totalRows}
          loading={table.isLoading}
          columnVisibilityModel={visibilityModel}
          onColumnVisibilityModelChange={setVisibilityModel}
          checkboxSelection={false}
          customProps={{
            tableInstance: TableInstance.Inventory,
            apiRef,
            model,
            toolbar: {
              title: t('common:inventory'),
              toolbarActions: (
                <>
                  <Button
                    variant='contained'
                    size='large'
                    onClick={() =>
                      onUpdateDialogState(DialogName.MapInventoryToProduct, {
                        open: true,
                        errors: [],
                        data: null,
                      })
                    }
                  >
                    {t('products:mapInventory')}
                  </Button>
                </>
              ),
              rowActions: [
                {
                  type: 'default',
                  button: {
                    label: t('products:map'),
                    onClick: (apiRef, rowId) => {
                      const selectedRow = apiRef.current.getRow(
                        rowId || '',
                      ) as InventoryTableRow;
                      onUpdateDialogState(DialogName.MapInventoryToProduct, {
                        open: true,
                        data: selectedRow,
                        errors: [],
                      });
                    },
                  },
                },
                {
                  type: 'default',
                  button: {
                    label: t('products:unMap'),
                    divider: true,
                    onClick: (apiRef, rowId) => {
                      const selectedRow = apiRef.current.getRow(
                        rowId || '',
                      ) as InventoryTableRow;
                      onUpdateDialogState(DialogName.UnMapInventoryToProduct, {
                        open: true,
                        data: selectedRow,
                        errors: [],
                      });
                    },
                  },
                },
                {
                  type: 'default',
                  button: {
                    label: t('products:updateQuantity'),
                    onClick: (apiRef, rowId) => {
                      const selectedRow = apiRef.current.getRow(
                        rowId || '',
                      ) as InventoryTableRow;

                      window.open(
                        `${AppPath.Inventory}/${selectedRow.inventory_id}`,
                        'rel=noopener noreferrer',
                      );
                    },
                  },
                },
              ],
            },
          }}
        />
      </div>

      {dialog?.mapInventoryToProduct?.open && (
        <MapInventoryToProductDialog
          initialData={
            dialog.mapInventoryToProduct.data as InventoryTableRow | undefined
          }
          loading={dialog.mapInventoryToProduct.loading}
          errors={dialog.mapInventoryToProduct.errors}
          product={productRef.current}
          onClose={() =>
            onUpdateDialogState(DialogName.MapInventoryToProduct, {
              open: false,
            })
          }
          onMapProduct={(value) => {
            void handleMapInventoryToProduct(value);
          }}
        />
      )}

      {dialog?.unMapInventoryToProduct?.open && (
        <UnMapInventoryConfirmationDialog
          loading={dialog.unMapInventoryToProduct.loading}
          errors={dialog.unMapInventoryToProduct.errors}
          onClose={() =>
            onUpdateDialogState(DialogName.UnMapInventoryToProduct, {
              open: false,
            })
          }
          proceed={() => {
            const data = dialog.unMapInventoryToProduct
              ?.data as InventoryTableRow;

            void handleUnMap({
              productId: data.product_id,
              variantId: data.variant_id,
            });
          }}
        />
      )}
    </>
  );
};

export default InventoryTable;
